import { useEffect, useRef, useState } from "react";
import { Form, Radio, Input, Row, Col } from "antd";
import { UploadOutlined } from "@ant-design/icons";

import IconButton from "../../ui/IconButton";
import FormHeader from "../FormHeader";
import { CompanyIdentificationValue, CompanyIdentificationType, CompanyPlaceholderName } from "../../../types/newLinkBuilder";
import useFetchCompanies from "../../../utils/useFetchCompanies";
import useDebounce from "../../../utils/useDebounce";
import TagsInputField from "../../TagsInputField";

interface CompanyIdentificationFormProps {
    value: CompanyIdentificationValue;
    setValue: (value: CompanyIdentificationValue | ((prev: CompanyIdentificationValue) => CompanyIdentificationValue)) => void;
    handlePlaceholder: (name: CompanyPlaceholderName) => void;
}

const CompanyIdentificationForm: React.FC<CompanyIdentificationFormProps> = ({ value, setValue, handlePlaceholder }) => {
    const [searchValue, setSearchValue] = useState<string>("")
    const { companyOptions, fetchCompanies } = useFetchCompanies();
    const debounceValue = useDebounce(searchValue, 500);
    const [fetching, setFetching] = useState(false);
    const handleCompanyIdentificationChange = (field: keyof CompanyIdentificationValue, newValue: string) => {
        const company = JSON.parse(newValue)
        setValue((prev) => ({
            ...prev,
            [field]: company.symbol
        }))
    }

    const handleRadioChange = (type: CompanyIdentificationType) => {
        setValue((prev) => ({
            ...prev,
            selected: type
        }))
    }

    const searchInputRef = useRef<any>(null)
    useEffect(() => {
        searchInputRef.current?.focus()
    }, [])

    useEffect(() => {
        setSearchValue(value.searchInput)
    }, [value.searchInput])

    useEffect(() => {
        async function fetchData() {
            if (!debounceValue) return;
            setFetching(true);
            await fetchCompanies(debounceValue);
            setFetching(false);
        }

        fetchData();
    }, [debounceValue]);

    return (
        <div>
            <FormHeader title="Company Identification" />
            <Form.Item>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Search Section */}
                    <Row align="middle">
                        <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                            <Radio 
                                name="companyIdentification"
                                value={value.selected}
                                checked={value.selected === CompanyIdentificationType.Search}
                                onChange={() => handleRadioChange(CompanyIdentificationType.Search)}
                            >
                                Search
                            </Radio>
                        </Col>
                        <Col span={18} style={{ display: 'flex', justifyContent: 'start' }}>
                            <TagsInputField
                                disabled={value.selected === CompanyIdentificationType.CellReference}
                                placeholder="Search for a company"
                                onChange={(value) => handleCompanyIdentificationChange("searchInput", value)}
                            />
                        </Col>
                    </Row>

                    {/* Cell Reference Section */}
                    <Row align="middle">
                        <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                            <Radio 
                                name="companyIdentification"
                                value={value.selected}
                                checked={value.selected === CompanyIdentificationType.CellReference}
                                onChange={() => handleRadioChange(CompanyIdentificationType.CellReference)}
                            >
                                Cell Reference
                            </Radio>
                        </Col>
                        <Col span={18} style={{ display: 'flex', justifyContent: 'start', flexDirection: 'column', gap: '10px' }}>
                            <Row>
                                <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                    <span>- Exchange</span>
                                </Col>
                                <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                                    <Input
                                        placeholder="Exchange"
                                        disabled={value.selected === CompanyIdentificationType.Search}
                                        value={value.exchange}
                                        onChange={(e) => handleCompanyIdentificationChange("exchange", e.target.value)}
                                    />
                                </Col>
                                <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                                    <IconButton
                                        disabled={value.selected === CompanyIdentificationType.Search}
                                        icon={<UploadOutlined />}
                                        onClick={() => handlePlaceholder(CompanyPlaceholderName.CompanyIdentificationExchangeCellReference)}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                    <span>- Ticker</span>
                                </Col>
                                <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                                    <Input
                                        placeholder="Ticker"
                                        disabled={value.selected === CompanyIdentificationType.Search}
                                        value={value.ticker}
                                        onChange={(e) => handleCompanyIdentificationChange("ticker", e.target.value)}
                                    />
                                </Col>
                                <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                                    <IconButton
                                        disabled={value.selected === CompanyIdentificationType.Search}
                                        icon={<UploadOutlined />}
                                        onClick={() => handlePlaceholder(CompanyPlaceholderName.CompanyIdentificationTickerCellReference)}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            </Form.Item>
        </div>
    )
}

export default CompanyIdentificationForm; 